// Список контактов. Только чтение: правки уходят в Bitrix, где на них висят
// бизнес-процессы с заданиями живым людям.
import { useMemo, useState } from 'react'

import { useCompanyTitles, useContacts, useFields, useRefs } from '../api/hooks'
import { подсказкаПустойДаты } from '../api/istochnik'
import type { ListParams } from '../api/types'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { isVesselContact } from '../lib/entities'
import { firstValue, fmtDate, fmtText } from '../lib/format'

const PAGE = 50   // страница ядра

/**
 * Кого показывать. Суда в Bitrix — это контакты с фамилией «Судно», лежат они в той же
 * таблице, и их примерно половина от 21 тысячи записей: без разделения список выглядит
 * сломанным.
 *
 * Ядро умеет отсеять их только в одну сторону. «Только суда» — это `%LAST_NAME=Судно`,
 * обычный LIKE, счётчик и страницы при этом честные. Обратного оно не умеет: оператор
 * `!%` ядро отклоняет, а `!LAST_NAME` — точное неравенство и промахивается мимо
 * «Судно (Арх.)». Поэтому «только люди» отсеиваем уже на пришедшей странице и прямо
 * пишем, сколько строк спрятали: соврать счётчиком хуже, чем показать неполную страницу.
 */
type Show = 'all' | 'people' | 'vessels'

const SHOW_LABEL: Record<Show, string> = {
  all: 'людей и суда',
  people: 'только людей',
  vessels: 'только суда',
}

export default function Contacts() {
  const [name, setName] = useState('')
  const [show, setShow] = useState<Show>('all')
  const [start, setStart] = useState(0)

  const refs = useRefs()
  // названия колонок — из справочника ядра: переименуют поле в Bitrix, переименуется и здесь
  const f = useFields('contact')

  const params = useMemo<ListParams>(() => {
    const filter: Record<string, unknown> = {}
    // %FULL_NAME — колонка ядра «Имя Отчество Фамилия». По ней «иван петров» находится
    // целиком; в самом Bitrix такого поля нет, это витрина ядра.
    if (name.trim()) filter['%FULL_NAME'] = name.trim()
    if (show === 'vessels') filter['%LAST_NAME'] = 'Судно'
    return {
      filter,
      order: { DATE_MODIFY: 'DESC' },
      start,
      select: ['ID', 'NAME', 'LAST_NAME', 'SECOND_NAME', 'COMPANY_ID', 'PHONE',
               'ASSIGNED_BY_ID', 'DATE_MODIFY'],
    }
  }, [name, show, start])

  const q = useContacts(params)
  const data = q.data

  const page = data?.items ?? []
  const rows = show === 'people' ? page.filter((c) => !isVesselContact(c)) : page
  const hiddenOnPage = page.length - rows.length

  // Названия компаний одним запросом на страницу: EntityLink их намеренно не грузит,
  // иначе на 50 строк вышло бы 50 запросов.
  const titles = useCompanyTitles(rows.map((c) => c.COMPANY_ID))

  const resetAnd = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  // Подписи колонок считаются один раз и идут и в шапку, и в data-label ячейки: на телефоне
  // таблица разбирается на карточки, шапки там нет, а две копии подписи разъехались бы при
  // первом переименовании поля в Bitrix. Тот же приём, что в PaymentsTable.
  const З = {
    id: 'ID',
    lastName: f.title('LAST_NAME'),
    name: f.title('NAME'),
    company: f.title('COMPANY_ID'),
    phone: f.title('PHONE'),
    assigned: f.title('ASSIGNED_BY_ID'),
    modified: f.title('DATE_MODIFY'),
  }

  return (
    <>
      <div className="page-head">
        <h1>Контакты</h1>
        {/* «в вашей зоне» рядом с числом, а не только в оговорке ниже: счётчик читают
            отдельно от текста, и «найдено: 1» без этой приписки — часть выдана за целое. */}
        <span className="muted">
          {data && !data.stale
            ? `найдено: ${data.total}${data.scope?.limited ? ' (в вашей зоне)' : ''}`
            : ''}
        </span>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="contacts">
          <div className="f" style={{ flex: 1, minWidth: 240 }}>
            <label htmlFor="cn">Имя, отчество или фамилия</label>
            <input id="cn" value={name} onChange={(e) => resetAnd(setName)(e.target.value)}
                   placeholder="например: иван петров" />
          </div>

          <div className="f">
            <label htmlFor="cs">Показывать</label>
            <select id="cs" value={show} onChange={(e) => resetAnd(setShow)(e.target.value as Show)}>
              {(Object.keys(SHOW_LABEL) as Show[]).map((k) => (
                <option key={k} value={k}>{SHOW_LABEL[k]}</option>
              ))}
            </select>
          </div>
        </FilterBar>

        <div className="hint" style={{ marginTop: 10 }}>
          Судно в Bitrix — это контакт с фамилией «Судно»: такие строки помечены и ведут
          в карточку судна, а не контакта.
          {show === 'people' && ' Отбор «только людей» ядро не поддерживает, поэтому суда'
            + ' убираются уже на странице: счётчик и постраничная разбивка считают их вместе'
            + ' с остальными.'}
          {show === 'vessels' && ' Отбор «только суда» идёт в ядре поиском «Судно» по фамилии —'
            + ' счётчик и страницы честные.'}
        </div>
      </div>

      <QueryState isLoading={q.isLoading} error={q.error} stale={data?.stale} ageMin={data?.ageMin}
                  scope={data?.scope}>
        <div className="card">
          {/* `cards` — на телефоне таблица разбирается на карточки (styles.css, §12). */}
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">{З.id}</th>
                  <th>{З.lastName}</th>
                  <th>{З.name}</th>
                  <th>{З.company}</th>
                  <th className="nowrap">{З.phone}</th>
                  <th className="nowrap">{З.assigned}</th>
                  <th className="nowrap">{З.modified}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  // У судна своя карточка: там IMO, порт и сделки по этому судну.
                  const vessel = isVesselContact(c)
                  const kind = vessel ? 'vessel' : 'contact'
                  const given = [c.NAME, c.SECOND_NAME].filter(Boolean).join(' ')
                  return (
                    <tr key={c.ID}>
                      <td className="mono nowrap" data-label={З.id}><EntityLink kind={kind} id={c.ID} title={c.ID} /></td>
                      <td className="nowrap" data-label={З.lastName}>
                        <EntityLink kind={kind} id={c.ID} title={fmtText(c.LAST_NAME)} />
                        {vessel && <span className="badge" style={{ marginLeft: 6 }}>судно</span>}
                      </td>
                      {/* Пусто — так и пишем: фамилия уже в соседней колонке, повторять её
                          здесь значит показать судно дважды одним и тем же текстом. */}
                      <td data-label={З.name}>{given
                        ? <EntityLink kind={kind} id={c.ID} title={given} />
                        : <span className="muted">—</span>}</td>
                      <td data-label={З.company}>
                        <EntityLink kind="company" id={c.COMPANY_ID}
                                    title={titles.data?.[String(c.COMPANY_ID)]} />
                      </td>
                      <td className="nowrap muted" data-label={З.phone}>{fmtText(firstValue(c.PHONE))}</td>
                      <td className="nowrap" data-label={З.assigned}>{refs.userName(c.ASSIGNED_BY_ID)}</td>
                      {/* В своей базе времени правки Bitrix нет: прочерк с подсказкой. */}
                      <td className="nowrap muted" data-label={З.modified}
                          title={подсказкаПустойДаты(c.DATE_MODIFY)}>{fmtDate(c.DATE_MODIFY, true)}</td>
                    </tr>
                  )
                })}
                {data && !data.stale && rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty">
                      {/* «в вашей зоне таких контактов нет» и «таких контактов нет» — разные
                          утверждения: во втором человек ищет дальше, в первом — идёт к тому,
                          у кого зона шире. Про суда говорим раньше: там страница не пуста,
                          просто отфильтрована уже здесь, и права ни при чём. */}
                      <div className="empty-title">
                        {hiddenOnPage > 0
                          ? `На этой странице только суда (${hiddenOnPage}) — людей нет. Ядро не умеет пропустить их, листайте дальше.`
                          : data.scope?.limited
                            ? 'В вашей зоне таких контактов нет — выборка сужена правами, а не фильтрами'
                            : 'Ничего не найдено под эти фильтры'}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {hiddenOnPage > 0 && rows.length > 0 && (
            <div className="hint" style={{ marginTop: 10 }}>
              На странице скрыто судов: {hiddenOnPage} из {page.length}.
            </div>
          )}

          {data && data.total > PAGE && (
            <div className="pager">
              <button disabled={start === 0 || q.isFetching}
                      onClick={() => setStart(Math.max(0, start - PAGE))}>← назад</button>
              <span className="muted">
                {start + 1}–{Math.min(start + PAGE, data.total)} из {data.total}
              </span>
              <button disabled={data.next === undefined || q.isFetching}
                      onClick={() => setStart(data.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}
